import { useTranslation } from "react-i18next";
import { Search } from "@digital-twins/design-system/icons";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import type { CreativeWorkspaceFiltersProps } from "@/types/creative-library";

export function CreativeWorkspaceFilters({
  value,
  brands,
  onChange,
}: CreativeWorkspaceFiltersProps) {
  const { t } = useTranslation("app");
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="creative-workspace-filters"
    >
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          value={value.query}
          onChange={(e) => onChange({ ...value, query: e.target.value })}
          placeholder={t("creativeLibrary.search")}
          aria-label={t("creativeLibrary.search")}
          className="pl-9"
          data-testid="creative-library-search"
        />
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Select
          value={value.brand}
          onValueChange={(brand) => onChange({ ...value, brand })}
        >
          <SelectTrigger
            aria-label={t("creativeLibrary.filterBrand")}
            data-testid="creative-library-brand-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("creativeLibrary.allBrands")}
            </SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand.token} value={brand.token}>
                {brand.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={value.scope}
          onValueChange={(scope) => onChange({ ...value, scope })}
        >
          <SelectTrigger
            aria-label={t("creativeLibrary.scope")}
            data-testid="creative-library-scope-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["all", "mine", "shared", "organization"] as const).map((scope) => (
              <SelectItem key={scope} value={scope}>
                {t(`creativeLibrary.scopes.${scope}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={value.origin}
          onValueChange={(origin) => onChange({ ...value, origin })}
        >
          <SelectTrigger
            aria-label={t("creativeLibrary.originLabel")}
            data-testid="creative-library-origin-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("creativeLibrary.allOrigins")}
            </SelectItem>
            {(["upload", "generated"] as const).map((origin) => (
              <SelectItem key={origin} value={origin}>
                {t(`creativeLibrary.origin.${origin}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={value.role}
          onValueChange={(role) => onChange({ ...value, role })}
        >
          <SelectTrigger
            aria-label={t("creativeLibrary.referenceRole")}
            data-testid="creative-library-role-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("creativeLibrary.allRoles")}</SelectItem>
            {(["product", "logo", "style", "composition", "identity"] as const).map(
              (role) => (
                <SelectItem key={role} value={role}>
                  {t(`creativeLibrary.roles.${role}`)}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
